import { ReactElement, useEffect, useState } from 'react'
import { I18n } from 'src/i18n'
import { Thumbnail } from 'src/ui-components'
import { Hyperlink } from 'src/ui-components/util/Hyperlink'
import styled from 'styled-components'
import { EditablePart } from './interfaces'
import { defaultTheme } from 'src/theme'
import {
  getImageUrl,
  getPartImages,
} from '../../../../../../helpers/partImageHelpers'

interface Props {
  part: EditablePart
  initialImgUrl?: string
  showButtons?: boolean
  onEdit?: (part: EditablePart) => void
  onRemove?: (part: EditablePart) => void
}

const PartSummary = (props: Props): ReactElement => {
  const { part, initialImgUrl, showButtons, onEdit, onRemove } = props
  const { partNum, lineCode, description } = part
  const [imgUrl, setImgUrl] = useState<string>(initialImgUrl)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (initialImgUrl && initialImgUrl !== '') return

    const partDetail = {
      partNumber: partNum,
      lineCode,
    }

    setLoading(true)
    getPartImages({ parts: [partDetail] }).then((resp) => {
      const updatedImgUrl = getImageUrl({
        linecode: lineCode,
        partnumber: partNum,
        partImages: resp,
      })
      setImgUrl(updatedImgUrl)
      setLoading(false)
    })
  }, [initialImgUrl, lineCode, partNum])

  const handleEdit = (): void => {
    onEdit(part)
  }

  const handleRemove = (): void => {
    onRemove(part)
  }

  return (
    <Wrapper>
      <ThumbnailWrapper>
        <Thumbnail urlOrBase64={loading ? '' : imgUrl} width={72} height={72} />
      </ThumbnailWrapper>
      <VerticallyCenteredColumn>
        <FlexColumn>
          <Description>{description}</Description>
          <Flex>
            <span>
              <I18n name="partNum" /> <b>{partNum}</b> {' | '}
              <I18n name="lineCode" /> <b>{lineCode}</b>
            </span>
          </Flex>
        </FlexColumn>
        {showButtons && (
          <ButtonsWrapper>
            <Hyperlink action={handleEdit}>Edit</Hyperlink>
            <Hyperlink action={handleRemove} color={defaultTheme.colors.red}>
              Remove
            </Hyperlink>
          </ButtonsWrapper>
        )}
      </VerticallyCenteredColumn>
    </Wrapper>
  )
}

const Flex = styled.div`
  display: flex;
`

const FlexColumn = styled(Flex)`
  flex-direction: column;
`

const Wrapper = styled(Flex)`
  gap: 16px;
  margin-bottom: 32px;
`

const VerticallyCenteredColumn = styled(FlexColumn)`
  justify-content: center;
  gap: 16px;
`

const ThumbnailWrapper = styled(Flex)`
  align-items: center;
  font-size: 11px;
`

const Description = styled.div`
  font-family: Roboto;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
`

const ButtonsWrapper = styled(Flex)`
  gap: 12px;
`

export default PartSummary
